import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { neon } from "@/integrations/neon";
import { getErrorMessage, logError } from "@/utils/errors";
import { generateSecureToken } from "@/utils/random";
import type { Script, AvailableForm } from "@/features/dashboard/types";
import { useToast } from "@/hooks/use-toast";
import { startSpan } from "@/utils/sentry";

type ToastHandler = ReturnType<typeof useToast>["toast"];

interface UseDashboardDataOptions {
  userId: string;
  toast: ToastHandler;
}

interface UseDashboardDataReturn {
  scripts: Script[];
  isLoading: boolean;
  isFetching: boolean;
  userForms: AvailableForm[];
  availableFormsByScript: Record<string, AvailableForm[]>;
  refetchScripts: () => Promise<Script[]>;
  loadUserForms: (force?: boolean) => Promise<AvailableForm[]>;
  addFormToScript: (script: Script, formId: string) => Promise<void>;
  deleteScript: (scriptId: string) => Promise<void>;
}

const scriptsQueryKey = (userId: string) => ["dashboard", "scripts", userId] as const;
const formsQueryKey = (userId: string) => ["dashboard", "forms", userId] as const;

const fetchScripts = async (userId: string): Promise<Script[]> => {
  const data = await startSpan(
    {
      op: "neon.query",
      name: "scripts.select",
      attributes: { userId },
    },
    async () => {
      return neon.api.scripts.list();
    }
  );

  return (data as unknown as Script[]) ?? [];
};

const fetchUserForms = async (userId: string): Promise<AvailableForm[]> => {
  const data = await startSpan(
    {
      op: "neon.query",
      name: "feedback_forms.select",
      attributes: { userId },
    },
    async () => {
      return neon.api.forms.list();
    }
  );

  return (data as unknown as AvailableForm[]) ?? [];
};

export const useDashboardData = ({ userId, toast }: UseDashboardDataOptions): UseDashboardDataReturn => {
  const queryClient = useQueryClient();

  const scriptsQuery = useQuery({
    queryKey: scriptsQueryKey(userId),
    queryFn: () => fetchScripts(userId),
    enabled: Boolean(userId),
    staleTime: 30_000,
    onError: (error) => {
      logError(error, { feature: "dashboard", action: "fetchScripts", userId });
      toast({
        title: "Error loading scripts",
        description: getErrorMessage(error, "Failed to load scripts"),
        variant: "destructive",
      });
    },
  });

  const formsQuery = useQuery({
    queryKey: formsQueryKey(userId),
    queryFn: () => fetchUserForms(userId),
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    onError: (error) => {
      logError(error, { feature: "dashboard", action: "loadUserForms", userId });
      toast({
        title: "Error loading forms",
        description: getErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    },
  });

  const availableFormsByScript = useMemo(() => {
    const scripts = scriptsQuery.data ?? [];
    const userForms = formsQuery.data ?? [];

    if (userForms.length === 0) {
      return {} as Record<string, AvailableForm[]>;
    }

    return scripts.reduce((acc, script) => {
      const currentVersionId = script.current_version_id;

      const linkedFormIdsForCurrentVersion = new Set(
        script.script_forms
          .filter((scriptForm) => scriptForm.script_version_id === currentVersionId)
          .map((scriptForm) => scriptForm.feedback_forms.id)
      );

      acc[script.id] = userForms.filter((form) => !linkedFormIdsForCurrentVersion.has(form.id));
      return acc;
    }, {} as Record<string, AvailableForm[]>);
  }, [scriptsQuery.data, formsQuery.data]);

  const resolveVersionId = (script: Script) => {
    if (script.current_version_id) {
      return script.current_version_id;
    }

    if (!script.script_versions || script.script_versions.length === 0) {
      return null;
    }

    return script.script_versions.reduce((latest, version) => {
      if (!latest) return version;
      if ((version.version_number ?? 0) > (latest.version_number ?? 0)) {
        return version;
      }
      return latest;
    }, script.script_versions[0]).id;
  };

  const addFormMutation = useMutation({
    mutationKey: ["dashboard", "add-form", userId],
    mutationFn: async ({ script, formId }: { script: Script; formId: string }) => {
      const reviewLink = generateSecureToken(24);
      const versionId = resolveVersionId(script);

      if (!versionId) {
        throw new Error("No version found for this script. Please create a version first.");
      }

      await startSpan(
        {
          op: "neon.insert",
          name: "script_forms.insert",
          attributes: { scriptId: script.id, formId },
        },
        async () => {
          return neon.api.scripts.addForm({
            scriptId: script.id,
            formId,
            reviewLink,
            scriptVersionId: versionId,
          });
        }
      );
    },
    onSuccess: async (_, variables) => {
      toast({
        title: "Form added successfully",
        description: "The feedback form has been linked to your script.",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: scriptsQueryKey(userId) }),
        queryClient.invalidateQueries({ queryKey: formsQueryKey(userId) }),
      ]);

      return variables;
    },
    onError: (error, variables) => {
      logError(error, {
        feature: "dashboard",
        action: "addFormToScript",
        scriptId: variables?.script.id,
        formId: variables?.formId,
      });
      toast({
        title: "Error adding form",
        description: getErrorMessage(error, "Failed to add form"),
        variant: "destructive",
      });
    },
  });

  const deleteScriptMutation = useMutation({
    mutationKey: ["dashboard", "delete-script", userId],
    mutationFn: async (scriptId: string) => {
      await startSpan(
        {
          op: "neon.delete",
          name: "scripts.delete",
          attributes: { scriptId },
        },
        async () => {
          return neon.api.scripts.delete(scriptId);
        }
      );
    },
    onSuccess: async () => {
      toast({
        title: "Script deleted",
        description: "The script has been successfully deleted.",
      });

      await queryClient.invalidateQueries({ queryKey: scriptsQueryKey(userId) });
    },
    onError: (error, scriptId) => {
      logError(error, { feature: "dashboard", action: "deleteScript", scriptId });
      toast({
        title: "Error deleting script",
        description: getErrorMessage(error, "Failed to delete script"),
        variant: "destructive",
      });
    },
  });

  const refetchScripts = useCallback(async () => {
    const result = await scriptsQuery.refetch();
    return result.data ?? [];
  }, [scriptsQuery]);

  const loadUserForms = useCallback(async (force: boolean = false) => {
    if (!force && formsQuery.data) {
      return formsQuery.data;
    }

    const result = await formsQuery.refetch();
    return result.data ?? [];
  }, [formsQuery]);

  const addFormToScript = useCallback(
    async (script: Script, formId: string) => {
      await addFormMutation.mutateAsync({ script, formId });
    },
    [addFormMutation]
  );

  const deleteScript = useCallback(
    async (scriptId: string) => {
      await deleteScriptMutation.mutateAsync(scriptId);
    },
    [deleteScriptMutation]
  );

  return {
    scripts: scriptsQuery.data ?? [],
    isLoading: scriptsQuery.isLoading,
    isFetching: scriptsQuery.isFetching,
    userForms: formsQuery.data ?? [],
    availableFormsByScript,
    refetchScripts,
    loadUserForms,
    addFormToScript,
    deleteScript,
  };
};
